import type { TextRun } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  SHAPE_TEXT_INSET_BOTTOM,
  SHAPE_TEXT_INSET_TOP,
} from '@/lib/text/constants/shape-text-insets'
import type { ShapeTextFlow } from './types/shape-text-flow'
import type { ShapeTextLayoutResult } from './types/shape-text-layout-result'
import type { ShapeTextLayoutOptions } from './types/shape-text-layout-options'
import { getShapeTextFlowForRowPlan, shapeTextEdgeClearancePt } from './helpers/get-shape-text-flow'
import { layoutShapeTextContent } from './helpers/layout-shape-text-content'
import { applyPerLineLeadingToFlow } from './helpers/per-line-leading'

/** Hard ceiling on rows added by one reclaim pass, so a degenerate pitch cannot run away. */
const MAX_RECLAIMED_ROWS = 64

/**
 * How many times the reclaim may re-probe. Each accepted pass strictly increases `consumedLength`,
 * so this is a cost bound rather than a correctness one — but a pass re-probes the outline and
 * re-flows the whole frame, and this runs per keystroke through the live chain projection.
 *
 * More than one is needed because the extension inherits the settled stack's own last row: reclaiming
 * at a fine pitch can produce a shorter final row, which lets the NEXT pass reach further down a
 * frame the first pass had to stop short of.
 */
const MAX_RECLAIM_PASSES = 4

/**
 * How many times a settled layout is re-probed at the positions its own re-stack moved rows to (see
 * `layoutShapeTextWithPerLineLeading`). One pass normally settles it: the rows are probed where they
 * are drawn, and re-stacking rows that already sit at their own leading leaves them in place. A
 * further pass is only needed when the re-flow put different words on a line and so changed that
 * line's leading.
 */
const MAX_RESTACK_REPROBE_PASSES = 3

/** Whether the re-stack moved any row away from where the flow it was laid out in probed it. */
function rowsMovedByRestack(
  settled: ShapeTextFlow['rows'],
  probed: ShapeTextFlow['rows']
): boolean {
  return settled.some((row) => !probed.some((p) => Math.abs(p.yTop - row.yTop) < 0.01))
}

/**
 * Flow text through a shape and settle it at its real per-line leading — the whole two-pass
 * pipeline, including the reclaim pass that keeps `consumedLength` honest.
 *
 * **Why a second pass exists.** The row grid is built at ONE frame-wide pitch, and
 * `buildShapeTextMeasurer` takes that pitch from the run with the largest `parsedLeadingPt`
 * (`rowMetricRun`). `applyPerLineLeadingToFlow` then re-stacks each row at its OWN leading, which is
 * by construction ≤ the frame maximum. So the moment a frame holds mixed leading or mixed point
 * sizes — which is what an external paste produces, since `forcePointSize` normalises the pasted
 * size but nothing normalises the pasted `line-height` — the grid is taller than the text that gets
 * painted into it. The re-stack pulls everything up, real space opens at the bottom, and
 * `consumedLength` still reports the taller grid's capacity: the overflow badge lights on a frame
 * the user can see has room. That is WMPCM-10022.
 *
 * Measured on a 72×72 rectangle at 8pt with one 14pt-leading run: the grid fits 5 rows at pitch 14,
 * the re-stack lands them at 0/14/28/37.6/47.2, and 13.2pt — more than a whole row — is left unused
 * below the last line while 5 characters sit overset.
 *
 * **What the second pass does.** It re-probes the outline at the positions the re-stack actually
 * produced, extends that plan to the shape's real bottom at the last row's own pitch, and re-flows
 * the same text through it. Nothing is trusted about the retry: it is accepted only when it consumes
 * MORE, so this can add text to a frame and never remove it. That one-sidedness is what makes a
 * second pass safe to run unconditionally rather than something that needs its own convergence
 * proof — and it is the same shape as `convergeKeptOffset` in `fit-paragraphs-to-shape.ts`.
 *
 * Skipped entirely when the first pass already laid out every character, which is the common case.
 *
 * **`reapplyWrapAndRounding` — do not omit this when the caller has a wrap context.**
 * `getShapeTextFlowForRowPlan` re-probes rows from the shape's OWN outline alone (`flattenShapePath`
 * + `bandSegmentsAt`); it has no notion of external wrap-source obstacles or the shape's own rounded
 * corners, both of which were already subtracted from the ORIGINAL `flow` this function receives
 * (`shapeTextFlowWithWrap`, above the boundary of this module — this file cannot import that helper
 * directly without a circular dependency). Passing this callback lets the caller re-run that exact
 * correction on the freshly re-probed rows before the retry settle, exactly as
 * `shapeTextFlowWithWrap` did for the first pass's `flow`. Omit it only for a caller with no wrap
 * context at all (tests, or a shape confirmed to have no nearby wrap sources) — otherwise the retry
 * silently re-flows text straight through every wrap obstacle and rounded corner the moment the
 * first pass alone can't fit everything, since it accepts the retry whenever it consumes more.
 *
 * **`settleOverride` — lets a caller layer additional flow-aware layout underneath this same
 * two-pass reclaim structure, without duplicating it.** The default settle step is plain
 * `layoutShapeTextContent` + per-line leading; a caller that also needs, say, drop-cap placement
 * (`layoutShapeTextWithDropCap` in `shape-drop-cap.ts`, which cannot be imported from this module
 * without a circular dependency) passes its own settle function instead — it still receives the
 * exact same probe/retry candidates this function produces, and must still return the per-line-
 * leaded result, so the reclaim decision (`consumedLength` comparison) stays meaningful.
 */
export function layoutShapeTextWithPerLineLeading(
  shape: ShapeData,
  flow: ShapeTextFlow,
  text: string,
  measureTextPt: (value: string, charIndex?: number) => number,
  charRunMap: Array<TextRun | null>,
  paragraphAlignments?: number[],
  options?: ShapeTextLayoutOptions,
  reapplyWrapAndRounding?: (flow: ShapeTextFlow) => ShapeTextFlow,
  settleOverride?: (candidate: ShapeTextFlow) => {
    flow: ShapeTextFlow
    layout: ShapeTextLayoutResult
  }
): { flow: ShapeTextFlow; layout: ShapeTextLayoutResult } {
  const settle =
    settleOverride ??
    ((candidate: ShapeTextFlow) =>
      applyPerLineLeadingToFlow(
        candidate,
        layoutShapeTextContent(text, candidate, measureTextPt, paragraphAlignments, options),
        charRunMap
      ))

  // `flow.bottomBound` already carries the border clearance (`getShapeTextFlow` /
  // `getShapeTextFlowBoundary`); the literal is only reached by a hand-built fixture, so it has to
  // subtract the same amount rather than quietly reclaiming the border's ground.
  const clearancePt = shapeTextEdgeClearancePt(shape)
  const bottomBound = Math.max(
    SHAPE_TEXT_INSET_TOP + clearancePt,
    flow.bottomBound ?? shape.height - SHAPE_TEXT_INSET_BOTTOM - clearancePt
  )
  /** The settled rows, then more at the stack's finest pitch down to the shape's bottom. */
  const planFrom = (settled: ShapeTextFlow['rows']) => {
    const last = settled[settled.length - 1]
    // The SMALLEST pitch the settle actually produced, not the gap between the last two rows.
    // Reading only the last gap made the extension inherit whatever the second-to-last row happened
    // to be: one 48pt row near the bottom of an otherwise 12pt frame set `step` to 57.6 and
    // reclaimed a single row where four fit. The minimum is the finest rhythm the frame has shown it
    // can produce, and over-generating candidate rows is free — `applyPerLineLeadingToFlow` re-stacks
    // them at their real pitch and drops whatever no longer fits. With one row there is no gap to
    // read and its own thickness is the only honest guess.
    let step = last.rowHeight

    for (let i = 1; i < settled.length; i++) {
      step = Math.min(step, settled[i].yTop - settled[i - 1].yTop)
    }

    const plan = settled.map((row) => ({ yTop: row.yTop, rowHeight: row.rowHeight }))

    if (!(step > 0.01)) return plan

    // `last.rowHeight`, NOT `step`, stays the probed band height. `getShapeTextFlowForRowPlan` reads
    // the outline over exactly that band, so a shorter band on a tapering shape reports segments
    // wider than the row can really use and force-places glyphs outside the outline (WMPCM-10123).
    // Pitch and probe height are separate knobs; only the pitch moves here.
    for (let y = last.yTop + step; plan.length < settled.length + MAX_RECLAIMED_ROWS; y += step) {
      if (y + last.rowHeight > bottomBound + 0.1) break
      plan.push({ yTop: y, rowHeight: last.rowHeight })
    }

    return plan
  }
  const reprobe = (plan: Array<{ yTop: number; rowHeight: number }>) => {
    const replanned = getShapeTextFlowForRowPlan(shape, plan, flow)

    return reapplyWrapAndRounding ? reapplyWrapAndRounding(replanned) : replanned
  }

  let probed = flow
  let best = settle(flow)

  // The re-stack moves rows but keeps the segments they were probed with at their OLD position. When
  // the grid pitch is taller than the lines (text under the 9pt row floor, or a leading tighter than
  // the frame's tallest run) every row moves up a little more than the last, so a line can end up
  // drawn beside a different part of the outline, or of a wrap obstacle, than the one it was fitted
  // to: wrapped as if beside a star's widest point while drawn beside its tip, or run straight
  // through the star's lower points after being fitted to the clear row below them (WMPCM-10558).
  // Re-probe at the positions the rows are actually drawn and lay the text out again, until the
  // re-stack stops moving them. This result is the true geometry, so it is taken whether it holds
  // more text or less; the reclaim below then uses any room it freed. Frames whose rows the re-stack
  // leaves in place, which is every frame of uniform type at or above the row floor, skip it.
  for (let pass = 0; pass < MAX_RESTACK_REPROBE_PASSES; pass++) {
    if (best.flow.rows.length === 0 || !rowsMovedByRestack(best.flow.rows, probed.rows)) break

    probed = reprobe(planFrom(best.flow.rows))
    best = settle(probed)
  }

  if (best.layout.consumedLength >= text.length) return best

  for (let pass = 0; pass < MAX_RECLAIM_PASSES; pass++) {
    const settled = best.flow.rows

    if (settled.length === 0) break

    const plan = planFrom(settled)

    // Nothing was reclaimed — the settled stack already reaches the bottom, so a retry would re-flow
    // the identical geometry.
    if (plan.length === settled.length) break

    const retry = settle(reprobe(plan))

    // Strictly one-sided, which is also this loop's termination proof: `consumedLength` is bounded by
    // `text.length` and every accepted pass raises it, so it cannot cycle. A pass that gains nothing
    // stops rather than re-probing the same geometry forever.
    if (retry.layout.consumedLength <= best.layout.consumedLength) break

    best = retry

    if (best.layout.consumedLength >= text.length) break
  }

  return best
}
